import { createAsyncThunk } from "@reduxjs/toolkit";
import { offlineSearchCombined } from "../offlineSearchService";
import { searchApi } from "../../../api/searchApi";
import offlineOrTimeoutPromise from "../utils/offlineOrTimeoutPromise";
import { saveRecentSearches } from "../recentSearchService";
import {
  setApiReachable,
  setApiUnreachable,
} from "../../networkStatus/networkStatusSlice";
import updateFirstCalculatorUrl from "../utils/updateFirstCalculatorUrl";

const saveRecentSearchAndReturnPayload = (searchParams, results, online) => {
  const item = {
    searchText: searchParams.searchText,
    url: `/searchAction/?appendedinputbuttons=${searchParams.searchText}`,
  };
  const recentSearches = saveRecentSearches("combinedSearch", item);
  if (searchParams.previousResults) {
    results.topicResults.results = [...searchParams.previousResults, ...results.topicResults.results];
  }
  return {
    topicResults: results.topicResults,
    drugResults: results.drugResults,
    isOffline: !online,
    recentSearches,
  };
};

/**

 Redux Thunk that performs a combined search with online/offline fallback

 @param {Object|string} searchParams - Search parameters or search text string
 @param {string} searchParams.searchText - The search query text
 @param {number} [searchParams.top=50] - Maximum number of results to return
 @param {string} [searchParams.guidelineFacet] - Optional filter for specific guidelines
 @param {string} [searchParams.sortBy="relevance"] - Sort order for results
 @param {number} [searchParams.skip=0] - Number of results to skip (for pagination)
 @returns {Promise<object>} Promise resolving to search results with topic and drug results,
  and a flag indicating if results are from offline search
 */

export const combinedSearchThunk = createAsyncThunk(
  "enhancedSearch/combinedSearch",
  async (searchParams, { dispatch }) => {
    try {
      const apiPromise = searchApi.combinedSearch(searchParams);
      let results = await Promise.race([
        apiPromise,
        offlineOrTimeoutPromise(),
      ]);
      dispatch(setApiReachable());
      if (results.topicResults?.results?.length > 0) {
        results.topicResults.results = updateFirstCalculatorUrl(results.topicResults.results);
      }
      return saveRecentSearchAndReturnPayload(searchParams, results, true);
    } catch (error) {
      dispatch(setApiUnreachable());
      try {
        const offlineResults = await offlineSearchCombined(searchParams);
        return saveRecentSearchAndReturnPayload(
          searchParams,
          offlineResults,
          false,
        );
      } catch (offlineError) {
        // If offline fails too, return an empty result
        console.error(
          "Offline search also failed for combined search:",
          offlineError,
        );
        return saveRecentSearchAndReturnPayload(
          searchParams,
          { topicResults: {}, drugResults: [] },
          false,
        );
      }
    }
  },
);
